import { AddonAction } from 'models/AddonAction';
import { NotionalType } from 'models/common/notional';
import type { Custodian } from 'models/custodian';
import type { LoadingResult, MergeDeal } from 'models/forms/AddonFormResult';
import type { DealSplitData } from 'models/forms/DealSplitData';
import { SalesBeneficiary } from 'models/forms/SalesBeneficiary';
import { Item } from 'models/IItem';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import type { MapDispatchToProps } from 'react-redux';
import { connect } from 'react-redux';
import type { RemunerationMode, SolveMode, TradeClientSide } from 'state/addonForm';
import {
  addonActionChanged,
  addonBackToBackFileCodeChanged,
  addonBackToBackIsBackToBackChanged,
  addonBackToBackIsDelayedBackToBackChanged,
  addonBackToBackIsDeltaAdjustedChanged,
  addonBackToBackNoteNominalChanged,
  addonBackToBackTypeChanged,
  addonDailyNumberOfSharesChanged,
  addonDealSplitChanged,
  addonFixedAmountChanged,
  addonFundingLockDateChanged,
  addonMergeDealsRequestedChanged,
  addonNotionalChanged,
  addonNotionalTypeChanged,
  addonOfferPriceChanged,
  addonRemunerationModeChanged,
  addonSalesBeneficiaryChanged,
  addonSalesDeskChanged,
  addonSelectedOnBehalfClientChanged,
  addonSelectedTradeReferenceChanged,
  addonSolveModeChanged,
  addonTradeClientSideChanged,
  addonUpFrontFeeChanged,
  addonXMarginChanged,
} from 'state/addonForm/addonForm.action';
import type { QuoteFormError } from 'state/addonForm/addonForm.selector';
import * as addonSelector from 'state/addonForm/addonForm.selector';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { BackToBackType } from 'state/uiPanelState/tradeConfirmation/tradeConfirm.model';

interface OwnProps {
  tabId: string;
}

interface StateProps {
  productId: string;
  instrumentContractName?: string;
  wrapper?: string;
  fileCode?: string;
  isinCode?: string;
  isOtc?: boolean;
  isShareAccDec: boolean;
  tradeClientSide?: TradeClientSide;
  addonAction?: AddonAction;
  tradeClientSides: readonly TradeClientSide[];
  selectedOnBehalfClient?: ISalesCounterpart | null;
  custodians?: LoadingResult<readonly Custodian[]>;
  mergeDealsRequested?: boolean;
  mergeableDeals?: LoadingResult<readonly MergeDeal[]>;
  selectedTradeReference?: string | undefined;
  addonNotional?: number;
  addonNotionalInUnits?: number;
  dailyNumberOfShares?: number;
  totalNumberOfAccumulationDays?: number;
  currency?: string;
  fundingLockDate?: string;
  notionalType?: NotionalType;
  dealSplit?: readonly DealSplitData[];
  remunerationMode?: RemunerationMode;
  solveModes?: readonly SolveMode[];
  solveFor?: SolveMode;
  xMargin?: number;
  upFrontFee?: number;
  offerPrice?: number;
  fixedAmount?: number;
  validationErrors: readonly QuoteFormError[];
  isReadOnly: boolean;
  isLoading: boolean;
  salesBeneficiary?: string;
  salesDesk?: number;
  availableSalesBeneficiaries?: Item<SalesBeneficiary>[];
  availableSalesDesks?: Item<number>[];
  isBackToBack?: boolean;
  backToBackFileCode?: string;
  backToBackIsDeltaAdjusted?: boolean;
  backToBackNoteNominal?: number;
  backToBackType?: BackToBackType;
  initialBackToBackType?: BackToBackType;
  isDelayedBackToBack?: boolean;
}

interface DispatchProps {
  onAddonNotionalChange: (newAddonNotional: number | undefined) => void;
  onDailyNumberOfShareChanged: (newDailyNumberOfShares: number | undefined) => void;
  onNotionalTypeChange: (newNotionalType: NotionalType) => void;
  onSelectedOnBehalfClientChange: (newClient: ISalesCounterpart | null) => void;
  onDealSplitChange: (dealSplit: readonly DealSplitData[]) => void;
  onTradeClientSideChange: (newTradeClientSide: TradeClientSide) => void;
  onAddonActionChange: (newAddonAction: AddonAction) => void;
  onMergeDealsRequestedChange: (newMergedDealRequested: boolean) => void;
  onSelectedTradeReferenceChange: (newSelectedTradeReference: string | undefined) => void;
  onRemunerationModeChange: (newRemunerationMode: RemunerationMode) => void;
  onSolveModeChange: (newSolveMode: SolveMode) => void;
  onXMarginChange: (xMargin: number | undefined) => void;
  onUpFrontFeeChange: (upFrontFee: number | undefined) => void;
  onOfferPriceChange: (offerPrice: number | undefined) => void;
  onFixedAmountChange: (fixedAmount: number | undefined) => void;
  onFundingLockDateChange: (fundingLockDate: string | null) => void;
  onBackToBackFileCodeChange: (value: string | undefined) => void;
  onBackToBackIsBackToBackChange: (value: boolean) => void;
  onBackToBackIsDelayedBackToBackChange: (value: boolean | undefined) => void;
  onBackToBackIsDeltaAdjustedChange: (value: boolean | undefined) => void;
  onBackToBackNoteNominalChange: (value: number | undefined) => void;
  onBackToBackTypeChange: (value: BackToBackType) => void;
  onSalesBeneficiaryChange: (salesBeneficiary: string) => void;
  onSalesDeskChange: (salesDesk: string) => void;
}
export type AddonMainFormProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId }: OwnProps): StateProps => {
  return {
    productId: addonSelector.getProductId(state, tabId)!,
    instrumentContractName: addonSelector.getInstrumentContractName(state, tabId),
    wrapper: addonSelector.getWrapper(state, tabId),
    fileCode: addonSelector.getFileCode(state, tabId),
    isinCode: addonSelector.getIsinCode(state, tabId),
    isOtc: addonSelector.isOtc(state, tabId),
    isShareAccDec: addonSelector.isShareAccDec(state, tabId) ?? false,
    selectedOnBehalfClient: addonSelector.getSelectedOnBehalfClient(state, tabId),
    tradeClientSide: addonSelector.getTradeClientSide(state, tabId),
    tradeClientSides: addonSelector.getTradeClientSides(),
    addonAction: addonSelector.getAddonAction(state, tabId),
    custodians: addonSelector.getCustodians(state, tabId),
    mergeableDeals: addonSelector.getMergeableDeals(state, tabId),
    mergeDealsRequested: addonSelector.getMergeDealsRequested(state, tabId),
    selectedTradeReference: addonSelector.getSelectedTradeReference(state, tabId),
    notionalType: addonSelector.getNotionalType(state, tabId),
    currency: addonSelector.getCurrency(state, tabId),
    fundingLockDate: addonSelector.getFundingLockDate(state, tabId),
    addonNotional: addonSelector.getAddonNotional(state, tabId),
    addonNotionalInUnits: addonSelector.getAddonNotionalInUnits(state, tabId),
    dailyNumberOfShares: addonSelector.getDailyNumberOfShares(state, tabId),
    totalNumberOfAccumulationDays: addonSelector.getTotalNumberOfAccumulationDays(state, tabId),
    dealSplit: addonSelector.getDealSplit(state, tabId),
    remunerationMode: addonSelector.getRemunerationMode(state, tabId),
    solveModes: addonSelector.getSolveModes(state, tabId),
    solveFor: addonSelector.getSolveFor(state, tabId),
    xMargin: addonSelector.getXMargin(state, tabId),
    upFrontFee: addonSelector.getUpFrontFee(state, tabId),
    offerPrice: addonSelector.getOfferPrice(state, tabId),
    fixedAmount: addonSelector.getFixedAmount(state, tabId),
    validationErrors: addonSelector.getValidationErrors(state, tabId),
    isReadOnly: addonSelector.isReadOnly(state, tabId) ?? true,
    isLoading: addonSelector.isLoading(state, tabId),
    salesBeneficiary: addonSelector.getSalesBeneficiaryRtfeLogin(state, tabId),
    salesDesk: addonSelector.getSalesDesk(state, tabId),
    availableSalesBeneficiaries: addonSelector.getAvailableSalesBeneficiaries(state, tabId),
    availableSalesDesks: addonSelector.getAvailableSalesDesks(state, tabId),
    isBackToBack: addonSelector.isBackToBack(state, tabId),
    backToBackFileCode: addonSelector.getBackToBackFileCode(state, tabId),
    backToBackIsDeltaAdjusted: addonSelector.getBackToBackIsDeltaAdjusted(state, tabId),
    backToBackNoteNominal: addonSelector.getBackToBackNoteNominal(state, tabId),
    backToBackType: addonSelector.getBackToBackType(state, tabId),
    initialBackToBackType: addonSelector.getInfo(state, tabId)?.backToBackType as BackToBackType | undefined,
    isDelayedBackToBack: addonSelector.isDelayedBackToBack(state, tabId),
  };
};

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch: ThunkDispatch, { tabId }) => ({
  onAddonNotionalChange: newAddonNotional => {
    dispatch(addonNotionalChanged(tabId, newAddonNotional));
  },
  onDailyNumberOfShareChanged: newDailyNumberOfShares => {
    dispatch(addonDailyNumberOfSharesChanged(tabId, newDailyNumberOfShares));
  },
  onNotionalTypeChange: newNotionalType => {
    dispatch(addonNotionalTypeChanged(tabId, newNotionalType));
  },
  onSelectedOnBehalfClientChange: newClient => {
    dispatch(addonSelectedOnBehalfClientChanged(tabId, newClient));
  },
  onDealSplitChange: newDealSplit => {
    dispatch(addonDealSplitChanged(tabId, newDealSplit));
  },
  onTradeClientSideChange: newTradeClientSide => {
    dispatch(addonTradeClientSideChanged(tabId, newTradeClientSide));
  },
  onAddonActionChange: newAddonAction => {
    dispatch(addonActionChanged(tabId, newAddonAction));
  },
  onMergeDealsRequestedChange: newMergedDealRequested => {
    dispatch(addonMergeDealsRequestedChanged(tabId, newMergedDealRequested));
  },
  onSelectedTradeReferenceChange: newSelectedTradeReference => {
    dispatch(addonSelectedTradeReferenceChanged(tabId, newSelectedTradeReference));
  },
  onRemunerationModeChange: newRemunerationMode => {
    dispatch(addonRemunerationModeChanged(tabId, newRemunerationMode));
  },
  onSolveModeChange: newSolveMode => {
    dispatch(addonSolveModeChanged(tabId, newSolveMode));
  },
  onXMarginChange: xMargin => {
    dispatch(addonXMarginChanged(tabId, xMargin));
  },
  onUpFrontFeeChange: upFrontFee => {
    dispatch(addonUpFrontFeeChanged(tabId, upFrontFee));
  },
  onOfferPriceChange: offerPrice => {
    dispatch(addonOfferPriceChanged(tabId, offerPrice));
  },
  onFixedAmountChange: fixedAmount => {
    dispatch(addonFixedAmountChanged(tabId, fixedAmount));
  },
  onFundingLockDateChange: fundingLockDate => {
    dispatch(addonFundingLockDateChanged(tabId, fundingLockDate ?? undefined));
  },
  onBackToBackFileCodeChange: value => {
    dispatch(addonBackToBackFileCodeChanged(tabId, value));
  },
  onBackToBackIsBackToBackChange: value => {
    dispatch(addonBackToBackIsBackToBackChanged(tabId, value));
  },
  onBackToBackIsDelayedBackToBackChange: value => {
    dispatch(addonBackToBackIsDelayedBackToBackChanged(tabId, value));
  },
  onBackToBackIsDeltaAdjustedChange: value => {
    dispatch(addonBackToBackIsDeltaAdjustedChanged(tabId, value));
  },
  onBackToBackNoteNominalChange: value => {
    dispatch(addonBackToBackNoteNominalChanged(tabId, value));
  },
  onBackToBackTypeChange: value => {
    dispatch(addonBackToBackTypeChanged(tabId, value));
  },
  onSalesBeneficiaryChange: salesBeneficiary => {
    dispatch(addonSalesBeneficiaryChanged(tabId, salesBeneficiary));
  },
  onSalesDeskChange: salesDesk => {
    dispatch(addonSalesDeskChanged(tabId, Number(salesDesk)));
  },
});

export const connectAddonMainForm = connect<StateProps, DispatchProps, OwnProps, AppState>(
  mapStateToProps,
  mapDispatchToProps,
);
